import React, { useState, useEffect } from "react";
import { Message, Loader, Confirm } from "semantic-ui-react";
import { FeedbackService } from "@api/feedback/feedback";
import { AuditService } from "@api/audit/audit"; // ✅ Added
import DashboardHeader from "@pages/frontsite/Dashboard/DashboardHeader";
import { useFeedback } from "./hooks/useFeedback";
import FeedbackModal from "./components/FeedbackModal";
import EditFeedbackModal from "./components/EditFeedbackModal";
import CategoryColumn from "./components/CategoryColumn";
import MobileAccordion from "./components/MobileAccordion";
import {
  MIN_FEEDBACK_LENGTH,
  pageSize,
  categoryConfig,
  categoryOptions,
} from "./constants/const";

const Feedback = () => {
  const {
    feedbacks,
    isLoading,
    error,
    successMessage,
    userReady,
    isConnected,
    setError,
    setSuccessMessage,
    loadFeedbacks,
    getUserInfo,
  } = useFeedback();

  // Form states
  const [newFeedback, setNewFeedback] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Edit states
  const [editingFeedback, setEditingFeedback] = useState(null);
  const [editText, setEditText] = useState("");
  const [editCategory, setEditCategory] = useState("");
  const [isEditing, setIsEditing] = useState(false);

  // UI states
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null });
  const [showModal, setShowModal] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [activeAccordion, setActiveAccordion] = useState(0);
  const [markingAsRead, setMarkingAsRead] = useState({});
  const [markingAllAsRead, setMarkingAllAsRead] = useState({});
  const [currentPages, setCurrentPages] = useState({
    "General Feedback": 1,
    Suggestions: 1,
    "Bug Report": 1,
  });

  // ✅ Added: centralized audit logger (no IDs stored)
  const logAudit = async (action, feedback) => {
    const { userName } = getUserInfo();
    try {
      const feedbackInfo = feedback
        ? `Category: ${feedback.category},\nMessage: "${feedback.message}"`
        : "";
      await AuditService.api.createLog({
        action,
        details: `User ${
          userName || "Unknown"
        } ${action.toLowerCase()}.\n\nInfo:\n${feedbackInfo}`,
        created_by: userName || "Unknown",
      });
    } catch (err) {
      console.error("Audit logging failed:", err);
    }
  };

  // Effects
  useEffect(() => {
    if (successMessage || error) {
      setShowMessage(true);
      const timer = setTimeout(() => setShowMessage(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, error]);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Event handlers
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!newFeedback.trim()) return setError("Please enter your feedback.");
    if (!selectedCategory) return setError("Please select a category.");
    if (!isConnected) return setError("Server unavailable. Try later.");
    if (newFeedback.length < MIN_FEEDBACK_LENGTH)
      return setError(`Minimum ${MIN_FEEDBACK_LENGTH} characters required.`);

    setIsSubmitting(true);

    try {
      const { userName } = getUserInfo();
      const feedbackOptions = {
        category: selectedCategory,
        createdBy: isAnonymous
          ? null
          : userName !== "Unknown User"
          ? userName
          : null,
      };

      await FeedbackService.submitFeedback(newFeedback, feedbackOptions);

      // ✅ Audit log
      await logAudit("Created Feedback", {
        category: selectedCategory,
        message: newFeedback,
      });

      setSuccessMessage("Feedback posted successfully!");
      setNewFeedback("");
      setSelectedCategory("");
      setIsAnonymous(false);
      setShowModal(false);
      await loadFeedbacks();
      setCurrentPages({
        "General Feedback": 1,
        Suggestions: 1,
        "Bug Report": 1,
      });
    } catch (error) {
      setError(`Failed to post feedback: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMarkAsRead = async (id) => {
    const { isAllowedToEdit } = getUserInfo();
    if (!isAllowedToEdit) return;

    setMarkingAsRead((prev) => ({ ...prev, [id]: true }));

    try {
      const fb = feedbacks.find((f) => f.id === id);
      await FeedbackService.markFeedbackAsRead(id);

      // ✅ Audit log
      if (fb) await logAudit("Marked Feedback As Read", fb);

      setSuccessMessage("Feedback marked as read successfully!");
      await loadFeedbacks();
      setCurrentPages({
        "General Feedback": 1,
        Suggestions: 1,
        "Bug Report": 1,
      });
    } catch (error) {
      setError(`Failed to mark feedback as read: ${error.message}`);
    } finally {
      setMarkingAsRead((prev) => ({ ...prev, [id]: false }));
    }
  };

  const handleMarkAllAsRead = async (category) => {
    const { isAllowedToEdit } = getUserInfo();
    if (!isAllowedToEdit) return;

    const categoryFeedbacks = categorizedFeedbacks[category] || [];
    const unreadFeedbacks = categoryFeedbacks.filter((fb) => !fb.is_read);

    if (unreadFeedbacks.length === 0) {
      setSuccessMessage("All feedbacks in this category are already read!");
      return;
    }

    setMarkingAllAsRead((prev) => ({ ...prev, [category]: true }));

    try {
      for (const feedback of unreadFeedbacks) {
        await FeedbackService.markFeedbackAsRead(feedback.id);
      }

      // ✅ Audit log
      await logAudit(
        "Marked All Feedback As Read",
        null,
        `Category: ${category}, Count: ${unreadFeedbacks.length}`
      );

      setSuccessMessage(
        `Successfully marked ${unreadFeedbacks.length} feedback(s) as read!`
      );
      await loadFeedbacks();
      setCurrentPages({
        "General Feedback": 1,
        Suggestions: 1,
        "Bug Report": 1,
      });
    } catch (error) {
      setError(`Failed to mark all feedbacks as read: ${error.message}`);
    } finally {
      setMarkingAllAsRead((prev) => ({ ...prev, [category]: false }));
    }
  };

  const handleEditFeedback = (feedback) => {
    setEditingFeedback(feedback);
    setEditText(feedback.message);
    setEditCategory(feedback.category);
    setIsEditing(true);
    setError("");
  };

  const handleSaveEdit = async () => {
    if (!editText.trim()) {
      setError("Please enter your feedback.");
      return;
    }

    if (editText.length < MIN_FEEDBACK_LENGTH) {
      setError(`Minimum ${MIN_FEEDBACK_LENGTH} characters required.`);
      return;
    }

    if (!editCategory) {
      setError("Please select a category.");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      await FeedbackService.updateFeedback(editingFeedback.id, {
        message: editText,
        category: editCategory,
      });

      // ✅ Audit log
      await logAudit("Updated Feedback", {
        category: editCategory,
        message: editText,
      });

      setSuccessMessage("Feedback updated successfully!");
      setIsEditing(false);
      setEditingFeedback(null);
      setEditText("");
      setEditCategory("");
      await loadFeedbacks();
    } catch (error) {
      setError(`Failed to update feedback: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingFeedback(null);
    setEditText("");
    setEditCategory("");
    setError("");
  };

  const handleDelete = async (id) => {
    try {
      const fb = feedbacks.find((f) => f.id === id);
      await FeedbackService.deleteFeedbackWithConfirmation(id, true);

      // ✅ Audit log
      if (fb) await logAudit("Deleted Feedback", fb);

      setSuccessMessage("Post deleted successfully.");
      await loadFeedbacks();
      setCurrentPages({
        "General Feedback": 1,
        Suggestions: 1,
        "Bug Report": 1,
      });
    } catch (error) {
      setError(`Failed to delete post: ${error.message}`);
    }
    setDeleteConfirm({ open: false, id: null });
  };

  const openModalWithCategory = (category) => {
    setSelectedCategory(category);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setNewFeedback("");
    setSelectedCategory("");
    setIsAnonymous(false);
    setError("");
    setSuccessMessage("");
  };

  const handlePageChange = (category, page) => {
    setCurrentPages((prev) => ({ ...prev, [category]: page }));
  };

  // Data processing
  const categorizedFeedbacks = Object.keys(categoryConfig).reduce(
    (acc, category) => {
      const categoryFeedbacks = feedbacks
        .filter((fb) => {
          const fbCategory = fb.category || "General Feedback";
          return fbCategory === category;
        })
        .sort(
          (a, b) =>
            new Date(b.created_at || b.createdAt) -
            new Date(a.created_at || a.createdAt)
        );
      acc[category] = categoryFeedbacks;
      return acc;
    },
    {}
  );

  // Show loading until user is ready
  if (!userReady) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "3rem",
          backgroundColor: "#fff",
          borderRadius: "12px",
          margin: "2rem 0",
        }}
      >
        <Loader active inline="centered">
          Loading user session...
        </Loader>
      </div>
    );
  }

  return (
    <div
      style={{
        maxWidth: "100%",
        padding: isMobile ? "0.5rem" : "1rem",
        fontFamily: "sans-serif",
        backgroundColor: "#f8f9fa",
        minHeight: "86vh",
        backgroundImage: `linear-gradient(to bottom, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.8)), url(${process.env.PUBLIC_URL}/images/conference.jpg)`,
      }}
    >
      <DashboardHeader />

      {showMessage && (
        <Message
          positive={!!successMessage}
          negative={!!error}
          onDismiss={() => setShowMessage(false)}
          style={{ marginTop: "5rem" }}
          content={successMessage || error}
        />
      )}

      {isConnected === false && (
        <Message
          warning
          style={{ marginTop: "5rem" }}
          content="Warning: Unable to connect to feedback service. Some features may not work."
        />
      )}

      {isLoading ? (
        <div
          style={{
            textAlign: "center",
            padding: "3rem",
            backgroundColor: "#fff",
            borderRadius: "12px",
            margin: "2rem 0",
          }}
        >
          <Loader active inline="centered">
            Loading feedback...
          </Loader>
        </div>
      ) : (
        <div>
          {isMobile ? (
            <MobileAccordion
              categorizedFeedbacks={categorizedFeedbacks}
              currentPages={currentPages}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              onEdit={handleEditFeedback}
              onDelete={(id) => setDeleteConfirm({ open: true, id })}
              onMarkAsRead={handleMarkAsRead}
              onMarkAllAsRead={handleMarkAllAsRead}
              onOpenModal={openModalWithCategory}
              getUserInfo={getUserInfo}
              markingAsRead={markingAsRead}
              markingAllAsRead={markingAllAsRead}
              categoryConfig={categoryConfig}
              isLoading={isLoading}
              activeAccordion={activeAccordion}
              onAccordionClick={(e, titleProps) => {
                const { index } = titleProps;
                const newIndex = activeAccordion === index ? -1 : index;
                setActiveAccordion(newIndex);
              }}
            />
          ) : (
            <div
              style={{
                display: "flex",
                gap: "1rem",
                alignItems: "flex-start",
                minHeight: "500px",
                marginTop: "2rem",
              }}
            >
              {Object.keys(categoryConfig).map((category) => (
                <CategoryColumn
                  key={category}
                  category={category}
                  feedbacks={categorizedFeedbacks[category] || []}
                  currentPage={currentPages[category] || 1}
                  pageSize={pageSize}
                  onPageChange={handlePageChange}
                  onEdit={handleEditFeedback}
                  onDelete={(id) => setDeleteConfirm({ open: true, id })}
                  onMarkAsRead={handleMarkAsRead}
                  onMarkAllAsRead={handleMarkAllAsRead}
                  onOpenModal={openModalWithCategory}
                  getUserInfo={getUserInfo}
                  markingAsRead={markingAsRead}
                  markingAllAsRead={markingAllAsRead}
                  categoryConfig={categoryConfig}
                  isLoading={isLoading}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      <FeedbackModal
        isOpen={showModal}
        onClose={handleModalClose}
        onSubmit={handleSubmit}
        newFeedback={newFeedback}
        setNewFeedback={setNewFeedback}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        isAnonymous={isAnonymous}
        setIsAnonymous={setIsAnonymous}
        isSubmitting={isSubmitting}
        error={error}
        isMobile={isMobile}
        categoryOptions={categoryOptions}
        categoryConfig={categoryConfig}
        userName={getUserInfo().userName}
        MIN_FEEDBACK_LENGTH={MIN_FEEDBACK_LENGTH}
      />

      <EditFeedbackModal
        isOpen={isEditing}
        onClose={handleCancelEdit}
        onSave={handleSaveEdit}
        editText={editText}
        setEditText={setEditText}
        editCategory={editCategory}
        setEditCategory={setEditCategory}
        isSubmitting={isSubmitting}
        error={error}
        setError={setError}
        isMobile={isMobile}
        categoryOptions={categoryOptions}
        MIN_FEEDBACK_LENGTH={MIN_FEEDBACK_LENGTH}
      />

      <Confirm
        open={deleteConfirm.open}
        onCancel={() => setDeleteConfirm({ open: false, id: null })}
        onConfirm={() => handleDelete(deleteConfirm.id)}
        header="Delete Feedback"
        content="Are you sure you want to delete this feedback? This action cannot be undone."
        cancelButton="Cancel"
        confirmButton={{ content: "Delete", negative: true }}
        size="small"
      />
    </div>
  );
};

export default Feedback;
